import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Page,
  LegacyCard,
  IndexTable,
  Button,
  Tooltip,
  Layout,
  EmptyState,
  Select,
  InlineStack,
  Text,
} from "@shopify/polaris";
import {
  downloadFile,
  useSalesTrends,
  useShopifyChannelSalesOptions,
  useTotalsAndMax,
  useShopifyShop,
  useUser,
  processQueryParams,
  getParamValue,
  getNumberParamValue,
  getArrayParamValue,
} from "../../api_utils/requests";
import { commaDelimiter, formatMoney, formattedSalesRate } from "../../helper_functions/utils";
import SalesTrendsSearchFieldAndMoreFilters from "./support/SalesTrendsSearchFieldAndMoreFilters";
import ImportProgressBanner from "../home/support/ImportProgressBanner";
import { QuantityChangeModal } from "../common/QuantityChangeModal";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import { ErrorBanner } from "../common/ErrorBanner";
import PopoverWithCheckList from "../common/Filters/PopoverWithCheckList";
import { Footer } from "../common/Footer";
import { HorseLocationSelect } from "../common/Filters/HorseLocationSelect";
import { LinkText } from "../common/HorseVariant/LinkText";
import type { Option, SalesTrendsQueryParams } from "../../api_utils/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import { SalesTrendsTableImportingSkeleton } from "../common/skeletons";
import NiceSalesRate from "./support/NiceSalesRate";
import StockInfo from "./support/StockInfo";
import NiceRemainingDays from "./support/nice_remaining_days";
import { useSearchParams } from "react-router-dom";

const salesRateColumNames = [
  { title: "Variant" },
  { title: "Available", tooltipContent: "Currently available" },
  { title: "Ordered", tooltipContent: "Currently ordered" },
  {
    title: "Days available",
    tooltipContent:
      "Available means that the variant was in-stock (at the location(s)) and published (on the sales channel(s))",
  },
  { title: "Sales", tooltipContent: "During the sampling period, the number of pieces sold" },
  { title: "Revenue", tooltipContent: "During the sampling period, the revenue from the sales" },
  { title: "Sales rate", tooltipContent: "During the sampling period, the sales rate" },
  { title: "Days remaining", tooltipContent: "Days remaining until the variant sells out" },
  { title: "Stock info" },
] as NonEmptyArray<IndexTableHeading>;

const seasonalColumNames = [
  { title: "Variant" },
  { title: "Available", tooltipContent: "Currently available" },
  { title: "Ordered", tooltipContent: "Currently ordered" },
  { title: "Days available", tooltipContent: "Days the variant was available for purchase" },
  { title: "Last year's sales", tooltipContent: "Last year's sales during this period" },
  { title: "Revenue", tooltipContent: "Last year's revenue from sales" },
  { title: "Days remaining", tooltipContent: "Days remaining until the variant sells out" },
  { title: "Stock info" },
] as NonEmptyArray<IndexTableHeading>;

const seasonalSamplingPeriods = [
  { value: "30", label: "Next 30 days", key: "30" },
  { value: "90", label: "Next 90 days", key: "90" },
  { value: "180", label: "Next 180 days", key: "180" },
  { value: "365", label: "Next year", key: "365" },
];
const salesRateSamplingPeriods = [
  { value: "30", label: "Past 30 days", key: "30" },
  { value: "90", label: "Past 90 days", key: "90" },
  { value: "180", label: "Past 180 days", key: "180" },
  { value: "365", label: "Past year", key: "365" },
];

const TABS = {
  sales_rate: 0,
  seasonal: 1,
};

const COLLECTIONS = {
  [TABS.sales_rate]: "sales_rate",
  [TABS.seasonal]: "seasonal",
};

const emptyState = <EmptyState heading="No variants found" image="" />;

const salesTrendsHelpPageUrl = "https://horse-inventory.notion.site/Sales-Trends-62314ee9b55a468c8116027b8fb7239d";

export default function SalesTrendsIndex(): React.JSX.Element {
  const { data: shopifyShop } = useShopifyShop();
  const { readyForRecommendations } = shopifyShop;
  const [errorMessage, setErrorMessage] = useState("");

  // Parse URL search params
  const [, setSearchParams] = useSearchParams();
  const searchParams = useMemo(() => new URLSearchParams(document.location.search), []);

  const default_forecasting_method = "sales_rate";
  // TODO: Implement a reverse-`processQueryParams` which will automatically find arrays of values, etc.
  const forecasting_method = getParamValue(searchParams.get("forecasting_method"), default_forecasting_method);
  const default_sampling_period = "90";
  const sampling_period = getParamValue(searchParams.get("sampling_period"), default_sampling_period);
  const default_page = 1;
  const page = getNumberParamValue(searchParams.get("page"), default_page);
  const sale_shopify_channel_ids = getArrayParamValue(searchParams.getAll("sale_shopify_channel_ids[]"), "string", []);
  const horse_location_id = getParamValue(searchParams.get("horse_location_id"), "");

  const initialForecastingMethod: number = TABS[forecasting_method];
  const [forecastingMethod, setForecastingMethod] = useState<number>(initialForecastingMethod);
  const [samplingPeriod, setSamplingPeriod] = useState<string>(sampling_period);
  const [pageNum, setPageNum] = useState(page);
  const [saleShopifyChannelIds, setSaleShopifyChannelIds] = useState<string[]>(sale_shopify_channel_ids);
  const [horseLocationId, setHorseLocationId] = useState<string>(horse_location_id);

  const [filters, setFilters] = useState<Partial<SalesTrendsQueryParams>>({});

  const queryParams: Partial<SalesTrendsQueryParams> = useMemo(
    () => ({
      ...filters,
      horse_location_id: horseLocationId,
      sale_shopify_channel_ids: saleShopifyChannelIds,
      sampling_period: samplingPeriod,
      page: pageNum,
      forecasting_method: COLLECTIONS[forecastingMethod],
    }),
    [filters, horseLocationId, samplingPeriod, saleShopifyChannelIds, pageNum, forecastingMethod],
  );
  const { isPending: isLoading, data: salesTrendsData } = useSalesTrends(queryParams);
  const { data: shopifyChannels } = useShopifyChannelSalesOptions();
  const {
    data: {
      user: { currency: userCurrency },
    },
  } = useUser();
  const { data: totalsAndMax } = useTotalsAndMax(queryParams);

  const { rows, hasNext, hasPrev } = salesTrendsData || {};
  const salesTrends = rows || [];

  // Don't ever decrease the max-range of the slider
  let maxSalesRate = 1.9;
  if (totalsAndMax.max_sales_rate > maxSalesRate) {
    maxSalesRate = totalsAndMax.max_sales_rate;
  }

  useEffect(() => {
    const newSearchParams = processQueryParams({
      ...filters,
      horse_location_id: horseLocationId,
      sale_shopify_channel_ids: saleShopifyChannelIds,
      sampling_period: samplingPeriod === default_sampling_period ? undefined : samplingPeriod,
      page: pageNum === default_page ? undefined : pageNum,
      forecasting_method:
        COLLECTIONS[forecastingMethod] === default_forecasting_method ? undefined : COLLECTIONS[forecastingMethod],
    });
    // TODO: This is being called multiple times during initial render, which is causing the URL to change multiple times and is breaking the back button
    setSearchParams(newSearchParams);
  }, [setSearchParams, filters, horseLocationId, samplingPeriod, saleShopifyChannelIds, pageNum, forecastingMethod]);

  const onFilterChange = useCallback((newFilters: Partial<SalesTrendsQueryParams>) => {
    setPageNum(1);
    setFilters(newFilters);
  }, []);

  const exportCSV = useCallback(async () => {
    const filename = `Horse Sales Trends.csv`;
    await downloadFile(`/sales_trends.csv`, filename, queryParams);
  }, [queryParams]);

  const removeSalesShopifyChannel = useCallback((shopifyChannelIdToRemove: string) => {
    const newShopifyChannels = saleShopifyChannelIds.filter(
      (shopifyChannelId) => shopifyChannelId !== shopifyChannelIdToRemove,
    );
    setSaleShopifyChannelIds(newShopifyChannels);
  }, [saleShopifyChannelIds, setSaleShopifyChannelIds]);

  const setPageNumAndScroll = useCallback((newPageNum: number) => {
    setPageNum(newPageNum);
    document.getElementsByClassName("Polaris-IndexTable__Table")[0]?.scrollIntoView();
  }, []);
  const nextPage = useCallback(() => {
    if (hasNext) {
      setPageNumAndScroll(pageNum + 1);
    }
  }, [hasNext, pageNum, setPageNumAndScroll]);
  const prevPage = useCallback(() => {
    if (hasPrev) {
      setPageNumAndScroll(pageNum - 1);
    }
  }, [hasPrev, pageNum, setPageNumAndScroll]);

  const samplingPeriods = forecastingMethod === TABS.seasonal ? seasonalSamplingPeriods : salesRateSamplingPeriods;

  const pageMarkup = (
    <Page
      fullWidth
      primaryAction={
        <Button onClick={exportCSV} removeUnderline size="medium" variant="plain">
          Export CSV
        </Button>
      }
      title="Sales trends"
    >
      <ImportProgressBanner />
      <Layout>
        {errorMessage ? (
          <Layout.Section>
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section>
          <LegacyCard>
            <LegacyCard.Section>
              <InlineStack blockAlign="start" gap="300">
                <HorseLocationSelect horseLocationId={horseLocationId} setHorseLocationId={setHorseLocationId} />
                <Select
                  label="Sampling period"
                  labelHidden
                  onChange={setSamplingPeriod}
                  options={samplingPeriods satisfies Option[]}
                  value={samplingPeriod}
                />
                <PopoverWithCheckList
                  choices={shopifyChannels}
                  defaultTitle="Sales channels"
                  onChange={setSaleShopifyChannelIds}
                  onValueRemoved={removeSalesShopifyChannel}
                  selected={saleShopifyChannelIds}
                />
              </InlineStack>
            </LegacyCard.Section>
            <SalesTrendsSearchFieldAndMoreFilters
              forecastingMethod={forecastingMethod}
              maxSalesRate={maxSalesRate}
              setFilters={onFilterChange}
              setForecastingMethod={setForecastingMethod}
            />
            <IndexTable
              emptyState={emptyState}
              headings={forecastingMethod === TABS.seasonal ? seasonalColumNames : salesRateColumNames}
              itemCount={salesTrends.length}
              loading={isLoading}
              pagination={{
                hasPrevious: hasPrev,
                previousKeys: [37],
                onPrevious: prevPage,
                hasNext: hasNext,
                nextKeys: [39],
                onNext: nextPage,
              }}
              selectable={false}
            >
              <IndexTable.Row id="0:str" key={0} position={0} rowType="subheader">
                <IndexTable.Cell>Totals</IndexTable.Cell>
                <IndexTable.Cell>{totalsAndMax ? commaDelimiter(totalsAndMax.total_available) : null}</IndexTable.Cell>
                <IndexTable.Cell>{totalsAndMax ? commaDelimiter(totalsAndMax.total_ordered) : null}</IndexTable.Cell>
                <IndexTable.Cell />
                <IndexTable.Cell>{totalsAndMax ? commaDelimiter(totalsAndMax.total_sales) : null}</IndexTable.Cell>
                <IndexTable.Cell>
                  {totalsAndMax ? formatMoney(totalsAndMax.total_revenue, userCurrency) : null}
                </IndexTable.Cell>
                {forecastingMethod === TABS.sales_rate && (
                  <IndexTable.Cell>
                    {totalsAndMax && totalsAndMax.total_sales_rate !== null
                      ? formattedSalesRate({ sales_rate: totalsAndMax.total_sales_rate })
                      : null}
                  </IndexTable.Cell>
                )}
                <IndexTable.Cell />
                <IndexTable.Cell />
              </IndexTable.Row>
              {salesTrends.map((variant, index) => (
                <IndexTable.Row id={`${index + 1}:str`} key={`row:${variant.horse_variant_id}`} position={index + 1}>
                  <IndexTable.Cell>
                    <HorseVariant variant={variant} />
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <QuantityChangeModal
                      activatorText={variant.tracked ? variant.currently_available?.toString() || "0" : "∞"}
                      disabled={variant.discarded}
                      horseVariantId={variant.horse_variant_id}
                      setErrorMessage={setErrorMessage}
                      tracked={variant.tracked}
                    />
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {variant.discarded ? (
                      <Text as="p" tone="subdued">
                        -
                      </Text>
                    ) : variant.ordered > 0 ? (
                      <LinkText href={`/purchase_orders?search=${variant.variant_title || variant.product_title}`}>
                        <Text as="p" truncate>
                          {variant.ordered}
                        </Text>
                      </LinkText>
                    ) : (
                      variant.ordered
                    )}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Tooltip
                      content={`${
                        forecastingMethod === TABS.seasonal ? "Last year, t" : "T"
                      }he variant was available for purchase during ${
                        variant.available_days
                      } of the last ${samplingPeriod} days.`}
                    >
                      <Text as="span" tone={variant.discarded ? "subdued" : undefined}>
                        {variant.available_days}
                      </Text>
                    </Tooltip>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span" tone={variant.discarded ? "subdued" : undefined}>
                      {variant.sales_quantity}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span">{formatMoney(variant.sales_quantity * variant.price, userCurrency)}</Text>
                  </IndexTable.Cell>
                  {forecastingMethod === TABS.sales_rate && (
                    <IndexTable.Cell>
                      <NiceSalesRate
                        available_days={variant.available_days}
                        discarded={variant.discarded}
                        sales_quantity={variant.sales_quantity}
                        sales_rate={variant.sales_rate}
                      />
                    </IndexTable.Cell>
                  )}
                  <IndexTable.Cell>
                    <NiceRemainingDays
                      forecastingMethod={forecastingMethod}
                      samplingPeriod={Number(samplingPeriod)}
                      variant={variant}
                    />
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <StockInfo sales_trends_horse_variant={variant} />
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </LegacyCard>
        </Layout.Section>
      </Layout>
      <Footer pageTitle="Sales trends" url={salesTrendsHelpPageUrl} />
    </Page>
  );

  return readyForRecommendations ? (
    pageMarkup
  ) : (
    <SalesTrendsTableImportingSkeleton />
  );
}
